import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, Upload, Wand2 } from 'lucide-react';
import type { AutoAssignResponse, ZoneImportResponse, ZoneImportRow } from '@simpleacs/shared';
import { api } from '../lib/api';
import { useTasks } from '../lib/tasks';
import { WizardDialog } from '../components/WizardDialog';
import { Button, ErrorNotice, Spinner } from '../components/ui';

/**
 * The Map card's zone tools (2026-09-16): Auto-assign, Import and Export.
 *
 * Auto-assign and Import are both preview→apply: the dialog opens on a DRY
 * RUN of the same route (a read that may stay in the dialog) so the operator
 * sees what would land — which splitters take which zone, which polygon
 * becomes which zone and what the clip took off it — and Apply closes the
 * modal and runs the real call in the tasks tray, as every write dialog does.
 * Export is two plain links: the session cookie rides along, the API answers
 * an attachment, and the browser saves it.
 */

const ACCEPT = '.geojson,.json,.kml,application/geo+json,application/json,application/vnd.google-earth.kml+xml';

type ImportFile = { name: string; body: unknown };

/**
 * A KML file is sent as `{ kml: "<text>" }` and a GeoJSON one as the parsed
 * document. Decided by extension first and by the first character second, so
 * a `.txt` someone saved a KML into still goes the right way.
 */
async function readImportFile(file: File): Promise<ImportFile> {
  const text = await file.text();
  if (/\.kml$/i.test(file.name) || text.trimStart().startsWith('<')) return { name: file.name, body: { kml: text } };
  try {
    return { name: file.name, body: JSON.parse(text) as unknown };
  } catch {
    throw new Error(`${file.name} is neither JSON nor KML — export a GeoJSON or KML file and try again.`);
  }
}

export function ZoneTools({ canManage }: { canManage: boolean }) {
  const [autoAssign, setAutoAssign] = useState(false);
  const [importing, setImporting] = useState<ImportFile | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  return (
    <>
      {canManage && (
        <>
          <Button size="sm" icon={<Wand2 className="size-3.5" />} onClick={() => setAutoAssign(true)} title="Put splitters and ONUs into the zone whose boundary holds them">
            Auto-assign
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0] ?? null;
              e.target.value = '';
              if (!file) return;
              setFileError(null);
              readImportFile(file)
                .then(setImporting)
                .catch((err: unknown) => setFileError(err instanceof Error ? err.message : 'That file could not be read.'));
            }}
          />
          <Button size="sm" icon={<Upload className="size-3.5" />} onClick={() => fileRef.current?.click()} title="Create or redraw zones from a GeoJSON or KML file">
            Import
          </Button>
        </>
      )}
      <span className="flex items-center gap-1 text-xs text-muted" title="Download the zones as a file">
        <Download className="size-3.5" />
        Export
        <a className="text-primary hover:underline" href="/api/zones/export?format=geojson" download>
          GeoJSON
        </a>
        ·
        <a className="text-primary hover:underline" href="/api/zones/export?format=kml" download>
          KML
        </a>
      </span>
      {fileError && <span className="text-xs text-danger">{fileError}</span>}
      {autoAssign && <AutoAssignDialog onClose={() => setAutoAssign(false)} />}
      {importing && <ImportZonesDialog file={importing} onClose={() => setImporting(null)} />}
    </>
  );
}

const STEPS = [
  { key: 'preview', title: 'Preview' },
  { key: 'apply', title: 'Apply' },
];

const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

// --- auto-assign --------------------------------------------------------------

function AutoAssignDialog({ onClose }: { onClose: () => void }) {
  const tasks = useTasks();
  // Keyed on the moment the dialog opened: the preview is of the topology NOW,
  // never a cached answer from an earlier look.
  const [openedAt] = useState(() => Date.now());
  const preview = useQuery({
    queryKey: ['zones-auto-assign-preview', openedAt],
    queryFn: () => api.post<AutoAssignResponse>('/zones/auto-assign', { dryRun: true }),
    staleTime: Infinity,
    gcTime: 0,
  });
  const data = preview.data;
  const nothingToDo = !data || (data.splitters.assigned.length === 0 && data.onus.assigned === 0);

  const apply = () => {
    onClose();
    tasks.start({
      label: 'Auto-assign splitters and ONUs to zones',
      run: () => api.post<AutoAssignResponse>('/zones/auto-assign', { dryRun: false }),
      done: (r) =>
        `${plural(r.splitters.assigned.length, 'splitter')} and ${plural(r.onus.assigned, 'ONU')} placed; ${plural(r.splitters.unplaced, 'splitter')} and ${plural(r.onus.unplaced, 'ONU')} still in no zone.`,
      href: '/topology',
      invalidate: [['topology']],
    });
  };

  return (
    <WizardDialog
      title="Auto-assign to zones"
      subtitle="Each splitter with coordinates goes into the zone whose boundary holds it — the innermost when zones nest — and each ONU with no zone follows its splitter. Nothing on any chassis changes."
      steps={STEPS}
      currentStep={0}
      maxWidth="xl"
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" variant="primary" disabled={nothingToDo} onClick={apply}>
            Apply
          </Button>
        </div>
      }
    >
      {preview.isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner className="size-5" />
        </div>
      ) : preview.error ? (
        <ErrorNotice error={preview.error} />
      ) : data ? (
        <>
          <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
            <Stat label="Splitters to place" value={data.splitters.assigned.length} />
            <Stat label="ONUs to place" value={data.onus.assigned} />
            <Stat label="Splitters left in no zone" value={data.splitters.unplaced} />
            <Stat label="ONUs left in no zone" value={data.onus.unplaced} />
          </dl>
          {data.splitters.assigned.length > 0 && (
            <div>
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">Splitters that take a zone</h3>
              <ul className="max-h-48 divide-y divide-line overflow-y-auto rounded-lg border border-line text-sm">
                {data.splitters.assigned.map((s) => (
                  <li key={s.id} className="flex items-center justify-between gap-3 px-3 py-1.5">
                    <span className="text-ink">{s.name}</span>
                    <span className="text-muted">→ {s.zone}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {data.splitters.disagrees.length > 0 && (
            <div className="rounded-lg border border-warn/40 bg-warn-soft px-3 py-2 text-sm text-warn">
              <p>
                {plural(data.splitters.disagrees.length, 'splitter stands', 'splitters stand')} in a different zone from the one
                {data.splitters.disagrees.length === 1 ? ' it is' : ' they are'} placed in. An operator put{' '}
                {data.splitters.disagrees.length === 1 ? 'it' : 'them'} there, so auto-assign leaves{' '}
                {data.splitters.disagrees.length === 1 ? 'it' : 'them'} alone — move{' '}
                {data.splitters.disagrees.length === 1 ? 'it' : 'them'} by hand if the boundary is right.
              </p>
              <ul className="mt-1.5 max-h-40 space-y-0.5 overflow-y-auto">
                {data.splitters.disagrees.map((s) => (
                  <li key={s.id}>
                    <span className="font-medium">{s.name}</span> — placed in {s.zone}, stands in {s.inZone}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {nothingToDo && data.splitters.disagrees.length === 0 && (
            <p className="text-sm text-muted">
              Nothing to place: every splitter with coordinates inside a boundary already has its zone, and so does every ONU on one.
              {data.splitters.unplaced + data.onus.unplaced > 0 &&
                ' What is left in no zone has no coordinates, or stands outside every drawn boundary.'}
            </p>
          )}
        </>
      ) : null}
    </WizardDialog>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-line px-3 py-2">
      <dt className="text-[11px] uppercase tracking-wide text-faint">{label}</dt>
      <dd className="text-lg font-semibold text-ink">{value.toLocaleString()}</dd>
    </div>
  );
}

// --- import -------------------------------------------------------------------

const ACTION_TONE: Record<ZoneImportRow['action'], string> = {
  created: 'bg-success-soft text-success',
  updated: 'bg-primary-soft text-primary',
  skipped: 'bg-danger-soft text-danger',
};

function ImportZonesDialog({ file, onClose }: { file: ImportFile; onClose: () => void }) {
  const tasks = useTasks();
  const [openedAt] = useState(() => Date.now());
  const preview = useQuery({
    queryKey: ['zones-import-preview', file.name, openedAt],
    queryFn: () => api.post<ZoneImportResponse>('/zones/import?dryRun=true', file.body),
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
  });
  const data = preview.data;
  const created = data?.rows.filter((r) => r.action === 'created').length ?? 0;
  const updated = data?.rows.filter((r) => r.action === 'updated').length ?? 0;
  const skipped = data?.rows.filter((r) => r.action === 'skipped').length ?? 0;

  const apply = () => {
    onClose();
    tasks.start({
      label: `Import zones from ${file.name}`,
      run: () => api.post<ZoneImportResponse>('/zones/import', file.body),
      done: (r) => {
        const c = r.rows.filter((x) => x.action === 'created').length;
        const u = r.rows.filter((x) => x.action === 'updated').length;
        const s = r.rows.filter((x) => x.action === 'skipped').length;
        return `${plural(c, 'zone')} created, ${u} redrawn, ${s} skipped.`;
      },
      href: '/topology',
      invalidate: [['topology']],
    });
  };

  return (
    <WizardDialog
      title={`Import zones from ${file.name}`}
      subtitle="Each polygon becomes a zone, or redraws the zone with the same name. An imported ring yields to the zones already drawn — nothing on the map moves to make room for it."
      steps={STEPS}
      currentStep={0}
      maxWidth="2xl"
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" variant="primary" disabled={created + updated === 0} onClick={apply}>
            Apply
          </Button>
        </div>
      }
    >
      {preview.isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner className="size-5" />
        </div>
      ) : preview.error ? (
        <ErrorNotice error={preview.error} />
      ) : data ? (
        <>
          <p className="text-sm text-muted">
            {plural(created, 'zone')} to create, {updated} to redraw, {skipped} skipped
            {data.source === 'kml' ? ' — read as KML.' : '.'}
          </p>
          <div className="max-h-[45vh] overflow-y-auto rounded-lg border border-line">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-surface-2 text-left text-[11px] uppercase tracking-wide text-faint">
                <tr>
                  <th className="px-3 py-1.5">Zone</th>
                  <th className="px-3 py-1.5">What happens</th>
                  <th className="px-3 py-1.5">Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {data.rows.map((r, i) => (
                  <tr key={`${r.name}-${i}`}>
                    <td className="px-3 py-1.5 text-ink">{r.name}</td>
                    <td className="px-3 py-1.5">
                      <span className={`rounded-full px-2 py-0.5 text-[11px] ${ACTION_TONE[r.action]}`}>{r.action}</span>
                    </td>
                    <td className="px-3 py-1.5 text-xs text-muted">{r.error ?? r.notice ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.errors.length > 0 && (
            <div className="rounded-lg border border-warn/40 bg-warn-soft px-3 py-2 text-xs text-warn">
              <p className="font-medium">Not polygons, left out of the import:</p>
              <ul className="mt-1 list-disc space-y-0.5 pl-4">
                {data.errors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      ) : null}
    </WizardDialog>
  );
}
